import { MathJaxContext } from 'better-react-mathjax';
import React, { useState, useEffect } from 'react';
import { convertMathJax } from '../../utils/QuestionsUtils';

// NOTE: Ensure 'apiService' is imported or defined in your project scope.
// import apiService from './apiService'; 
const MathText = React.memo(({ content }) => {
    return (
        <MathJaxContext>
            {convertMathJax(content)}
        </MathJaxContext >
    )
});

const ConceptDetailsModal = ({ isOpen, onClose, concept }) => {
    // 1. ALL HOOKS MUST BE CALLED UNCONDITIONALLY
    const [conceptUnits, setConceptUnits] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState(null);

    // unitData only holds facts and unitMaterials (top-level)
    const [unitData, setUnitData] = useState({ facts: [], unitMaterials: [] });

    // isLoadingDetails tracks the fetching of the initial unit list and all nested data
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);

    // Effect 1: Fetch Units and all nested data when the modal opens/concept changes
    useEffect(() => {

        // Conditional guard/cleanup
        if (!isOpen || !concept) {
            setConceptUnits([]);
            setSelectedUnit(null);
            setUnitData({ facts: [], unitMaterials: [] });
            console.log("returning")
            return;
        }

        const fetchUnitsAndNestedData = async (concpt) => {
            // console.log(isOpen + " " + JSON.parse(concpt))

            setIsLoadingDetails(true);
            setConceptUnits([]);
            setSelectedUnit(null);
            setUnitData({ facts: [], unitMaterials: [] });

            try {
                let conceptObj = JSON.parse(concpt);
                setConceptUnits(conceptObj.conceptUnits);
                if (conceptUnits.length > 0) {
                    setSelectedUnit(conceptUnits[0]);
                }
            } catch (error) {
                console.error("Failed to fetch concept units and details:", error);
            } finally {
                setIsLoadingDetails(false);
            }
        };
        fetchUnitsAndNestedData(concept);

    }, [isOpen, concept?.id]);

    // Effect 2: Extract data from selectedUnit (NO FLATTENING)
    useEffect(() => {
        if (selectedUnit) {
            // 1. Get facts directly nested under the unit
            const facts = selectedUnit.conceptFacts || [];

            // 2. Get materials directly nested under the unit (top-level materials)
            const unitMaterials = selectedUnit.conceptMaterials || [];

            setUnitData({
                facts: facts,
                unitMaterials: unitMaterials,
            });
        } else {
            setUnitData({ facts: [], unitMaterials: [] });
        }
    }, [selectedUnit]);


    // Conditional early return after all Hooks have been called
    if (!isOpen || !concept) return null;

    // Destructure for cleaner JSX
    const { facts, unitMaterials } = unitData;

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-[70vh] flex flex-col">
                <div className="p-2 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-1xl font-bold text-gray-800">Concept Details: **{concept.name}**</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-900 text-1xl font-bold">
                        &times;
                    </button>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* Left Panel: Concept Units */}
                    <div className="w-1/4 p-4 border-r border-gray-200 overflow-y-auto">
                        <h4 className="text-sm font-semibold mb-2 text-gray-700">📚 Concept Units</h4>

                        {isLoadingDetails && !conceptUnits.length ? (
                            <p className="text-gray-500">Loading units...</p>
                        ) : conceptUnits.length === 0 ? (
                            <p className="text-gray-500 text-sm">No units assigned to this concept.</p>
                        ) : (
                            <ul className="space-y-2">
                                {conceptUnits.map(unit => (
                                    <li
                                        key={unit.id}
                                        onClick={() => setSelectedUnit(unit)}
                                        className={`p-2 rounded cursor-pointer text-sm transition-colors ${selectedUnit?.id === unit.id ? 'bg-gray-800 text-white font-bold' : 'bg-gray-100 hover:bg-gray-200'}`}
                                    >
                                        {unit.name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Right Panel: Unit Details */}
                    <div className="flex-1 p-4 overflow-y-auto">
                        {/* <h4 className="text-sm font-bold mb-4 text-gray-800 border-b pb-2">
                            Unit Details: **{selectedUnit?.name || 'Please select a unit'}**
                        </h4> */}

                        {isLoadingDetails && selectedUnit ? (
                            <p className="text-gray-500 text-center py-10">Loading unit details...</p>
                        ) : selectedUnit ? (
                            <div className="space-y-8">



                                {/* <hr className="border-gray-200" /> */}

                                {/* 💡 Concept Facts (Facts include ALL non-audit attributes and nested items) */}
                                <section>
                                    <h5 className="text-lg font-semibold text-gray-700 mb-3 border-l-4 border-blue-500 pl-2">
                                        💡 Concept Facts ({facts.length})
                                    </h5>
                                    <div className="space-y-6">
                                        {facts.map(fact => (
                                            <div key={fact.id} className="bg-white p-4 rounded-lg shadow-xl border border-blue-100 transition duration-300 hover:shadow-2xl">

                                                {/* Fact Header and Metadata (All Non-Audit Columns) */}
                                                <div className="grid grid-cols-3 gap-x-6 pb-3 border-b border-gray-100">
                                                    {/* Column 1 (2/3 width): Main Content */}
                                                    <div className="col-span-2 space-y-2 border-r pr-4">
                                                        <p className="text-base font-semibold text-gray-900">
                                                            **Fact:** <MathText content={fact.fact} />
                                                        </p>
                                                        <p className="text-sm text-gray-600">
                                                            **Description:** <MathText content={fact.description} />
                                                        </p>
                                                    </div>

                                                    {/* Column 2 (1/3 width): Metadata */}
                                                    <div className="col-span-1 space-y-1 text-sm pt-1">
                                                        <p className="text-gray-800">
                                                            **ID:** <span className="font-normal text-gray-600">{fact.id}</span>
                                                        </p>
                                                        <p className="text-gray-800">
                                                            **Code:** <span className="font-normal text-blue-600">{fact.code}</span>
                                                        </p>
                                                        <p className="text-gray-800">
                                                            **Order:** <span className="font-normal">{fact.order}</span>
                                                        </p>
                                                        <p className="text-gray-800">
                                                            **Skill Level:** <span className="font-normal text-green-700">{fact.skillLevel}</span>
                                                        </p>
                                                        <p className="text-gray-800">
                                                            **Active:** <span className="font-normal">{fact.isActive ? 'Yes' : 'No'}</span>
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* --- Nested Assessments & Materials --- */}

                                                {/* Nested Assessments (Excluding Audit Columns) */}
                                                {(fact.conceptAssessments?.length > 0) && (
                                                    <div className="mt-3 p-3 bg-purple-50 rounded-md border border-purple-200">
                                                        <p className="text-xs font-bold text-purple-700 mb-2">
                                                            📋 Related Assessments ({fact.conceptAssessments.length})
                                                        </p>
                                                        <ul className="space-y-2 text-xs">
                                                            {fact.conceptAssessments.map(a => (
                                                                <li key={a.code} className="p-2 bg-white rounded shadow-sm border border-purple-100">
                                                                    <span className="font-medium text-gray-800">Q:</span><MathText content={a.question} /> <br />
                                                                    <span className="font-medium text-gray-600">A:</span> <MathText content={a.answer} /><br />
                                                                    <span className="font-medium text-gray-800">R:</span> <MathText content={a.rationale} /> <br />
                                                                    <span className="font-medium text-gray-600">M:</span> <MathText content={a.misconception} />
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {/* Nested Materials (Excluding Audit Columns) - Tabular View */}
                                                {(fact.conceptMaterials?.length > 0) && (
                                                    <div className="mt-3 p-3 bg-teal-50 rounded-md border border-teal-200">
                                                        <p className="text-xs font-bold text-teal-700 mb-2">
                                                            📂 Related Materials ({fact.conceptMaterials.length})
                                                        </p>
                                                        <div className="overflow-x-auto">
                                                            <table className="min-w-full divide-y divide-teal-200 text-xs">
                                                                <thead className="bg-teal-100/50">
                                                                    <tr>
                                                                        <th className="py-1 px-2 text-left font-medium text-gray-600 uppercase w-1/3">Name</th>
                                                                        <th className="py-1 px-2 text-left font-medium text-gray-600 uppercase w-1/6">Code</th>

                                                                        <th className="py-1 px-2 text-left font-medium text-gray-600 uppercase w-1/6">Type</th>
                                                                        <th className="py-1 px-2 text-left font-medium text-gray-600 uppercase w-1/2">Link</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="bg-white divide-y divide-teal-200">
                                                                    {fact.conceptMaterials.map(m => (
                                                                        <tr key={m.code} className="hover:bg-teal-100/30">
                                                                            <td className="py-2 px-2 text-gray-800">{m.name}</td>
                                                                            <td className="py-2 px-2 text-gray-800">{m.code}</td>

                                                                            <td className="py-2 px-2 text-orange-600">{m.type}</td>
                                                                            <td className="py-2 px-2 truncate max-w-[150px]">
                                                                                <a
                                                                                    href={m.link}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                                                                    title={m.link}
                                                                                >
                                                                                    {/* Display truncated link */}
                                                                                    {m.link.length > 30 ? `${m.link.substring(0, 27)}...` : m.link}
                                                                                </a>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        {facts.length === 0 && <p className="text-gray-500 text-sm italic">No facts found for this unit.</p>}
                                    </div>
                                </section>
                                {/* 📂 Unit Materials (Top-level materials) */}
                                <section>
                                    <h5 className="text-lg font-semibold text-gray-700 mb-3 border-l-4 border-teal-500 pl-2">
                                        📂 Unit Materials ({unitMaterials.length})
                                    </h5>

                                    {unitMaterials.length === 0 ? (
                                        <p className="text-gray-500 text-sm italic">No top-level materials found for this unit.</p>
                                    ) : (
                                        <div className="overflow-x-auto shadow-lg rounded-lg border border-gray-200">
                                            <table className="min-w-full divide-y divide-gray-200 table-auto">
                                                <thead className="bg-gray-100">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Name</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Code</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Type</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">Link</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {unitMaterials.map((m) => (
                                                        <tr key={m.code} className="hover:bg-teal-50">
                                                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{m.name}</td>
                                                            <td className="px-6 py-4 text-sm text-teal-600">{m.code}</td>
                                                            <td className="px-6 py-4 text-sm text-orange-600">{m.type}</td>
                                                            <td className="px-6 py-4 text-sm truncate max-w-xs">
                                                                <a
                                                                    href={m.link}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                                                    title={m.link}
                                                                >
                                                                    {/* Display truncated link but keep link fully functional */}
                                                                    {m.link.length > 50 ? `${m.link.substring(0, 47)}...` : m.link}
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </section>
                            </div>
                        ) : (
                            <p className="text-gray-400 mt-10 text-center">Select a unit from the left panel to view its details.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConceptDetailsModal;